import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Lightbulb, Rocket, ArrowLeft, Sparkles, MessageCircle, Phone, Mail, CheckCircle2 } from "lucide-react";
import { SchedulingGuideLink } from "@/components/site/SchedulingGuideLink";
import { joinInterviewExperiencesWaitlist } from "@/lib/interview-experiences-waitlist.functions";

const WHATSAPP_NUMBER = "919381256707";
const DISPLAY_NUMBER = "9381256707";

const WHATSAPP_MESSAGE = encodeURIComponent(
  "Hi Harsha, I have a question about the WeLivdIt Interview Experiences community hub."
);

export const Route = createFileRoute("/interviewexperiences")({
  head: () => ({
    meta: [
      { title: "Interview Experiences — Launching Soon | WeLivdIt" },
      {
        name: "description",
        content:
          "An exclusive F-1 visa interview community hub with 1000+ real transcripts, officer question breakdowns, and success stories is launching soon.",
      },
      { property: "og:title", content: "Interview Experiences — Launching Soon | WeLivdIt" },
      {
        property: "og:description",
        content:
          "An exclusive F-1 visa interview community hub with 1000+ real transcripts, officer question breakdowns, and success stories is launching soon.",
      },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: InterviewExperiencesComingSoon,
});

function InterviewExperiencesComingSoon() {
  const joinWaitlist = useServerFn(joinInterviewExperiencesWaitlist);

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const isEmailValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isEmailValid(email)) {
      setStatus("error");
      setStatusMessage("Please enter a valid email address.");
      return;
    }

    setStatus("loading");
    setStatusMessage("");

    try {
      const result = await joinWaitlist({ data: { email, phone, source: "interview-experiences" } });
      setStatus("success");
      setStatusMessage(result.message);
      setEmail("");
      setPhone("");
    } catch (err) {
      setStatus("error");
      setStatusMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      aria-label="Coming soon"
      className="relative flex min-h-[85vh] flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-background via-blue-50/60 to-indigo-50/40 px-4 py-16 text-center"
    >
      {/* Decorative blurred orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 top-1/4 h-64 w-64 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="absolute -right-24 bottom-1/4 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-2xl">
        {/* Animated lightbulb */}
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-amber-300 to-orange-400 shadow-xl shadow-orange-300/40 ring-8 ring-orange-100/60">
          <Lightbulb
            className="h-12 w-12 text-white drop-shadow-sm"
            aria-hidden="true"
            strokeWidth={2}
          />
        </div>
        <Sparkles
          className="absolute left-1/2 top-0 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-amber-400 animate-pulse"
          aria-hidden="true"
        />

        {/* Launch badge */}
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1.5 text-sm font-semibold text-orange-600 shadow-sm">
          <Rocket className="h-4 w-4" aria-hidden="true" />
          <span>Launching Soon</span>
        </div>

        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
          Great things take a little time!{" "}
          <span className="inline-block animate-bounce">💡</span>
        </h1>

        <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
          We are currently building an exclusive community hub featuring{" "}
          <span className="font-semibold text-foreground">1000+ real F1 visa interview transcripts</span>,
          officer question breakdowns, and success stories.
          <br className="hidden sm:block" /> Stay tuned!
        </p>

        {/* Waitlist form */}
        <div className="mt-10 rounded-2xl border border-border/60 bg-white/80 p-6 shadow-xl shadow-blue-900/5 backdrop-blur-sm sm:p-8">
          <h2 className="text-lg font-semibold text-foreground">Get early access</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Drop your email & WhatsApp number and we will notify you the moment the hub opens.
          </p>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="text-left">
                <label htmlFor="waitlist-email" className="mb-1.5 block text-sm font-medium text-foreground">
                  Email address <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <input
                    id="waitlist-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-11 w-full rounded-lg border border-input bg-background pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              <div className="text-left">
                <label htmlFor="waitlist-phone" className="mb-1.5 block text-sm font-medium text-foreground">
                  WhatsApp number
                </label>
                <div className="relative">
                  <MessageCircle className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <input
                    id="waitlist-phone"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 93812 56707"
                    className="h-11 w-full rounded-lg border border-input bg-background pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>

            {status === "error" && (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
                {statusMessage}
              </p>
            )}

            {status === "success" && (
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-left text-sm text-emerald-700" role="status">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{statusMessage}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex h-11 w-full items-center justify-center rounded-full bg-gradient-cta px-6 text-sm font-semibold text-primary-foreground shadow-lg shadow-blue-900/15 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {status === "loading" ? "Saving..." : "Notify me first"}
            </button>
          </form>

          <div className="mt-4 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground sm:flex-row">
            <span>Or reach out directly:</span>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-medium text-emerald-600 hover:underline"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              WhatsApp
            </a>
            <span className="hidden sm:inline">•</span>
            <a href={`tel:${DISPLAY_NUMBER}`} className="inline-flex items-center gap-1.5 font-medium text-foreground hover:underline">
              <Phone className="h-4 w-4" aria-hidden="true" />
              {DISPLAY_NUMBER}
            </a>
          </div>
        </div>

        <SchedulingGuideLink className="mt-8 !px-0" />

        {/* Support note */}
        <p className="mx-auto mt-6 max-w-md rounded-xl border border-dashed border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          Facing website issues or have ideas for the hub? Reach Harsha directly on WhatsApp/call at{" "}
          <a href={`tel:${DISPLAY_NUMBER}`} className="font-semibold text-foreground hover:underline">
            {DISPLAY_NUMBER}
          </a>
          {" "}— he takes care of website-related updates.
        </p>

        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-input bg-background px-6 py-2.5 text-sm font-semibold text-foreground transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to Home
          </Link>
        </div>
      </div>
    </section>
  );
}
